"use client";
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FilterSidebar from './FilterSidebar';
import ProductGrid from './ProductGrid';
import Header from '../Header';
import Footer from '../Footer';
import { BASE_URL, UPLOAD_URL } from '../../config/apiConfig';

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page mounts
  }, []);

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    toyTypes: {},
    ageRange: [0, 20],
    sortBy: { Free: false, Premium: false }
  });

  const [currentPage, setCurrentPage] = useState(1);
 const [itemsPerPage, setItemsPerPage] = useState(9);

useEffect(() => {
  const handleResize = () => {
    const width = window.innerWidth;

    if (width < 640) {
      setItemsPerPage(10); // ✅ mobile
    } else if (width < 1024) {
      setItemsPerPage(6); // tablet
    } else {
      setItemsPerPage(9); // desktop
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  const [categoryApplied, setCategoryApplied] = useState(false);

  const selectedCategory = location.state?.category;

  // Scroll to top when route, page, or filters change
  // useEffect(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [location, currentPage, filters]);

  // Fetch products
  useEffect(() => {
  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BASE_URL}/products`);
      const json = await res.json();

      if (json.status === 200 && Array.isArray(json.data)) {
        const baseURL = `${UPLOAD_URL}/products/`;

        const products = json.data.map(product => {
          const ageDetail = product.details?.find(
            d => d.label?.toLowerCase() === "age range"
          );

          let minAge = 0, maxAge = 20;
          if (ageDetail?.value) {
            const match = ageDetail.value.match(/(\d+)\s*-\s*(\d+)/);
            if (match) {
              minAge = Number(match[1]);
              maxAge = Number(match[2]);
            }
          }

          return {
            ...product,

            // ✅ SAFE IMAGE HANDLING
            image:
              typeof product.image === "string" && product.image.startsWith("http")
                ? product.image
                : baseURL + (product.image || "null"),

            isPremium: product.price > 0 || product.isPremium === true,
            isFree: product.price === 0,
            minAge,
            maxAge,
          };
        });

        // console.log(products);
        setAllProducts(products);

        // Initialize categories filter
        const categoriesObj = {};
products.forEach(p => {
  const cat = p.category?.trim().toLowerCase();
  if (cat && !(cat in categoriesObj)) categoriesObj[cat] = false;
});

        products.forEach(p => {
          if (!(p.category in categoriesObj)) categoriesObj[p.category] = false;
        });
        setFilters(prev => ({ ...prev, toyTypes: categoriesObj }));
      } else {
        console.error("No data returned from API.");
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchProducts();
}, []);


  // Apply category passed via state
  useEffect(() => {
    if (selectedCategory && Object.keys(filters.toyTypes).length && !categoryApplied) {
      setFilters(prev => ({
        ...prev,
        toyTypes: { ...prev.toyTypes, [selectedCategory]: true }
      }));
      setCategoryApplied(true);
    }
  }, [selectedCategory, filters.toyTypes, categoryApplied]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const selectedToyTypes = Object.keys(filters.toyTypes).filter(key => filters.toyTypes[key]);
  const searchQuery = new URLSearchParams(location.search).get("search")?.toLowerCase().trim() || "";

  useEffect(() => {
    if (searchQuery) {
      console.log("Searching for:", searchQuery);
    }
  }, [searchQuery]);

  const filteredProducts = (allProducts || []).filter(product => {
   const categoryMatch =
  !selectedToyTypes.length ||
  !product.category ||
  selectedToyTypes.includes(product.category);

    const name = product?.name?.toLowerCase() || "";
    const category = product?.category?.toLowerCase() || "";
    const search = searchQuery?.toLowerCase() || "";

    const searchMatch =
      !search ||
      name.includes(search) ||
      category.includes(search);

    const [filterMinAge, filterMaxAge] = filters.ageRange;
    const ageMatch =
      product?.minAge >= filterMinAge && product?.maxAge <= filterMaxAge;

    let sortByMatch = true;
    if (filters.sortBy.Free && !filters.sortBy.Premium) sortByMatch = product?.isFree;
    else if (!filters.sortBy.Free && filters.sortBy.Premium) sortByMatch = product?.isPremium;

    return categoryMatch && searchMatch && ageMatch && sortByMatch;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = page => setCurrentPage(page);
  const handlePrevious = () => currentPage > 1 && handlePageChange(currentPage - 1);
  const handleNext = () => currentPage < totalPages && handlePageChange(currentPage + 1);

  const handleProductClick = id => {
    navigate(`/product/${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentCategoryName = () => {
    const selected = Object.keys(filters.toyTypes).filter(k => filters.toyTypes[k]);
    if (selected.length === 1) return selected[0];
    if (selected.length > 1) return "Multiple Categories";
    return "All Products";
  };

  return (
    <div>
      <Header />
      <main className="flex gap-7 p-10 mx-auto  max-w-7xl min-h-screen bg-neutral-50 max-xl:flex-col max-xl:gap-5 max-md:p-5 max-sm:p-4">
        <div className="flex-shrink-0 w-[285px] max-xl:w-full">
          <FilterSidebar filters={filters} onFiltersChange={handleFiltersChange} />
        </div>
        <section className="flex flex-col flex-1 gap-5 min-w-0">
          <header className="flex justify-between items-center max-sm:flex-col max-sm:items-start max-sm:gap-2">
            <h1 className="text-2xl font-bold leading-6 text-black">{getCurrentCategoryName()}</h1>
            <p className="text-lg leading-6 text-black">{filteredProducts.length} items</p>
          </header>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sky-950 mb-4"></div>
                <p className="text-lg text-gray-600">Loading products...</p>
              </div>
            </div>
          ) : (
            <ProductGrid
              products={paginatedProducts}
              onProductClick={handleProductClick}
              hasFilters={selectedToyTypes.length > 0 || searchQuery || filters.sortBy.Free || filters.sortBy.Premium}
              searchQuery={searchQuery}
            />
          )}

          {/* Pagination - only show if there are products */}
          {!loading && filteredProducts.length > 0 && (
            <nav className="flex gap-2.5 justify-center items-center max-sm:flex-wrap max-sm:gap-1.5" aria-label="Pagination Navigation">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className="p-2.5 font-bold text-black hover:text-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-sky-950 focus:ring-offset-2"
                aria-label="Go to previous page"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i+1}
                  className={`p-2.5 font-semibold rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-950 focus:ring-offset-2 ${
                    currentPage === i+1
                      ? 'bg-sky-950 text-white'
                      : 'text-black hover:bg-gray-100'
                  }`}
                  onClick={() => handlePageChange(i+1)}
                  aria-label={`Go to page ${i+1}`}
                  aria-current={currentPage === i+1 ? 'page' : undefined}
                >
                  {i+1}
                </button>
              ))}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="p-2.5 font-bold text-black hover:text-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-sky-950 focus:ring-offset-2"
                aria-label="Go to next page"
              >
                Next
              </button>
            </nav>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Products;
